import {
  type AskPunttMessage,
  type AskPunttProjectData,
  type AskPunttUserQuestion,
} from "@mg/schemas/src/christo/askPuntt";
import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowsInSimple,
  ArrowsOutSimple,
  CaretRight,
  CircleNotch,
  Stop,
  Upload,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Flex,
  IconButton,
  Text,
  TextArea,
  Tooltip,
} from "@radix-ui/themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { askPunttChatRoute } from "./chatRoute";
import {
  type AskPunttMessageExt,
  collectReviewTicketIds,
  connectorsArrayToRecord,
  findContextFileIndex,
  mergeMessageStatus,
} from "./chatUtils";
import { AskPunttDropZone } from "./components/AskPunttDropZone";
import {
  AssetViewerPane,
  type AssetViewerPaneHandle,
} from "./components/AssetViewerPane";
import { type CanvasContent } from "./components/canvas/types";
import { CanvasPane } from "./components/CanvasPane";
import { ChatMessage } from "./components/ChatMessage";
import {
  InputPlusMenu,
  type ConnectorOption,
} from "./components/InputPlusMenu";
import { ProjectLoadingPane } from "./components/ProjectLoadingPane";
import { ProjectReviewStatusBanner } from "./components/ProjectReviewStatusBanner";
import { ProjectStatusPane } from "./components/ProjectStatusPane";
import {
  type QuestionFormHandle,
  QuestionForm,
} from "./components/QuestionForm";
import {
  ReviewChecklist,
  TICKET_REVIEW_CHECKS,
} from "./components/ReviewChecklist";
import { SuggestedResponses } from "./components/SuggestedResponses";
import { ThinkingState } from "./components/ThinkingState";
import { TicketViewPane } from "./components/TicketViewPane";
import { CONNECTOR_ICONS } from "./constants";
import { useAttachedFiles } from "./hooks/useAttachedFiles";
import { useAutoScroll } from "./hooks/useAutoScroll";
import { useFileDrop } from "./hooks/useFileDrop";
import { useKillerFlowStream } from "./hooks/useKillerFlowStream";
import { useTicketViewData } from "./hooks/useTicketView";
import { askPunttRoute } from "./route";

import { PageHeader } from "../../components/PageHeader";
import { getGuidelines } from "../../services/brand-guidelines";
import { editComment } from "../../services/projects";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { isMeaningfulGigsOrDemo } from "../../utils/auth";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import {
  askPunttKeys,
  useAbortAskPunttMessage,
  useAskPunttChatHistory,
  useAskPunttStreamingState,
  useSendAskPunttMessage,
} from "../../utils/queries/askPuntt";
import { setDrawerOpen } from "../../utils/slices/ui";

const STREAM_POLL_MS = 500;
const FULL_POLL_MS = 3000;
const POLL_TIMEOUT_MS = 300_000;

// Per-ticket "review underway" messages: empty content + only thought/text
// agent steps. They look pending but are actually complete server-side events
// emitted by the project review workflow. Exported for regression testing
// (PR #3311 follow-up: stop misclassifying these as streaming on hydration).
export const isReviewStatusMessage = (m: AskPunttMessageExt): boolean =>
  m.role === "assistant" &&
  m.content === "" &&
  (m.agentSteps?.length ?? 0) > 0 &&
  (m.agentSteps ?? []).every(
    (s) =>
      (s as { tool?: string; type?: string }).tool === "thought" ||
      (s as { tool?: string; type?: string }).type === "text",
  );

// Returns true when the last message in a chat is a server-side pending
// assistant turn that the current session didn't create — i.e. the
// post-review auto-summary placeholder. Used to keep the poll alive until
// the summary resolves without the user having to refresh.
export const isPostReviewSummaryPending = (
  messages: AskPunttMessageExt[],
): boolean => {
  const last = messages[messages.length - 1];
  if (!last) return false;
  return (
    last.role === "assistant" &&
    (last.status === "pending" || !last.content) &&
    !isReviewStatusMessage(last)
  );
};

const SKILL_LABELS: Record<string, string> = {
  claim_generation: "Claims Verification",
  compliance_review: "Compliance Review",
  artwork_review: "Artwork Review",
  brief_creation: "Brief Writing",
  compare: "Brief Comparison",
  project: "Project",
  puntt_actions: "Puntt Capabilities",
  puntt_value: "Workflow Analysis",
};

export default function AskPunttChat() {
  const { chatId } = askPunttChatRoute.useParams();
  const queryClient = useQueryClient();
  const [waitingForAI, setWaitingForAI] = useState(false);
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
  // Tracks the most recently pending message ID so agentSteps merge still works
  // after pendingMessageId is cleared (prevents the flash-on-completion bug).
  const lastPendingIdRef = useRef<string | null>(null);
  // True while a background canvas task is still writing — keeps the streaming
  // poll alive after the text response is complete so the full canvas renders.
  const [canvasPolling, setCanvasPolling] = useState(false);

  const [reviewPolling, setReviewPolling] = useState(false);
  // True when the server has a pending assistant message that the current
  // session didn't create — i.e. the post-review summary placeholder appeared
  // in MongoDB while waitingForAI was already false. Stored as state so that
  // updates (via the effect below) trigger a re-render and cause refetchInterval
  // to flip to FULL_POLL_MS for the duration of the summary generation.
  const [serverHasPendingMessage, setServerHasPendingMessage] = useState(false);

  // Full document poll: 1s until we find the pending message, then slow to 3s
  const {
    data: chatData,
    isLoading,
    dataUpdatedAt,
  } = useAskPunttChatHistory(chatId, {
    refetchInterval: waitingForAI
      ? pendingMessageId
        ? FULL_POLL_MS
        : STREAM_POLL_MS
      : canvasPolling || reviewPolling || serverHasPendingMessage
        ? FULL_POLL_MS
        : false,
  });

  // Lightweight streaming poll: only the pending message's streaming fields.
  // Stays active while canvasPolling so background canvas writes reach the UI.
  const streamingMessageId =
    pendingMessageId ?? (canvasPolling ? lastPendingIdRef.current : null);
  const { data: streamingState } = useAskPunttStreamingState(
    chatId,
    streamingMessageId,
    {
      enabled: !!(streamingMessageId && (waitingForAI || canvasPolling)),
      refetchInterval: STREAM_POLL_MS,
    },
  );
  const sendMessage = useSendAskPunttMessage(chatId);
  const abortMessage = useAbortAskPunttMessage(chatId);
  const analytics = useAnalytics("ask_puntt_chat");
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);

  // --- Instrumentation: time-to-first-response ---
  const messageSentAtRef = useRef<number | null>(null);
  const ttfrFiredRef = useRef(false);

  // --- Instrumentation: session depth ---
  const sessionStartRef = useRef(Date.now());
  const messageCountRef = useRef(0);
  const workflowTypeRef = useRef<string | undefined>(undefined);
  const guidelinesQuery = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  const availableConnectors: ConnectorOption[] = useMemo(
    () =>
      (guidelinesQuery.data?.askPunttConnectors ?? [])
        .filter((c) => c.enabled && c.connected)
        .map((c) => ({
          id: c.connectorId,
          label: `${c.name} search`,
          icon: CONNECTOR_ICONS[c.connectorId] ?? "🔗",
        })),
    [guidelinesQuery.data?.askPunttConnectors],
  );

  const [inputMessage, setInputMessage] = useState("");
  const [sectionContext, setSectionContext] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [localMessages, setLocalMessages] = useState<AskPunttMessageExt[]>([]);
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
    localPreviewUrls,
  } = useAttachedFiles({
    onUploadError: (err) =>
      analytics.capture("ask_puntt_upload_error", {
        chat_id: chatId,
        ...errorAnalyticsPayload(err),
      }),
  });
  const { isDragOver, dropZoneProps, handlePaste } = useFileDrop({
    onDrop: addFiles,
  });
  const [canvasDismissed, setCanvasDismissed] = useState(false);
  const [ticketDismissed, setTicketDismissed] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null,
  );
  const [highlightPing, setHighlightPing] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadRef = useRef(true);
  const knownMessageCountRef = useRef(0);
  const waitingSinceRef = useRef<number | null>(null);
  const dataAtWaitStartRef = useRef<number | null>(null);
  const prevDataUpdatedAtRef = useRef<number>(0);

  useEffect(() => {
    if (waitingForAI) {
      waitingSinceRef.current = Date.now();
    } else {
      waitingSinceRef.current = null;
      dataAtWaitStartRef.current = null;
      setPendingMessageId(null);
    }
  }, [waitingForAI]);

  // Clean up highlight ping timer on unmount
  useEffect(() => {
    return () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
    };
  }, []);

  // Keep lastPendingIdRef in sync so we can merge agentSteps even after
  // pendingMessageId is cleared to null.
  useEffect(() => {
    if (pendingMessageId) {
      lastPendingIdRef.current = pendingMessageId;
    }
  }, [pendingMessageId]);

  // Sync server messages with local state and detect completed assistant
  // responses.  We use `dataUpdatedAt` (changes on every successful refetch)
  // instead of `chatData?.messages` alone — TanStack Query's structural
  // sharing can return the same array reference when the deep content hasn't
  // changed from its perspective, which would prevent the effect from firing.
  useEffect(() => {
    if (!chatData?.messages) return;

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      const messages = chatData.messages;
      knownMessageCountRef.current = messages.length;
      setLocalMessages(messages);
      const last = messages[messages.length - 1];
      if (
        messages.length > 0 &&
        (last.role === "user" ||
          (last.role === "assistant" &&
            (last.status === "pending" || !last.content) &&
            !isReviewStatusMessage(last)))
      ) {
        setWaitingForAI(true);
      }
      return;
    }

    const dataChanged = dataUpdatedAt !== prevDataUpdatedAtRef.current;
    if (dataChanged) {
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      setLocalMessages((prev) => {
        const mergeId = pendingMessageId ?? lastPendingIdRef.current;
        if (!mergeId) return chatData.messages;
        const localPending = prev.find((m) => m._id === mergeId);
        if (!localPending) return chatData.messages;
        return chatData.messages.map((m) =>
          m._id === mergeId ? mergeMessageStatus(localPending, m) : m,
        );
      });
      knownMessageCountRef.current = chatData.messages.length;
    }

    if (!waitingForAI) return;

    if (
      waitingSinceRef.current &&
      Date.now() - waitingSinceRef.current > POLL_TIMEOUT_MS
    ) {
      setWaitingForAI(false);
      return;
    }

    if (!dataChanged) return;

    // Skip response detection until we get fresh data after waiting started.
    // Without this, the effect fires with stale chatData (still showing the
    // previous assistant response) and immediately stops polling.
    if (
      dataAtWaitStartRef.current !== null &&
      dataUpdatedAt <= dataAtWaitStartRef.current
    ) {
      return;
    }

    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.content &&
      lastMsg.status !== "pending"
    ) {
      setWaitingForAI(false);
      dataAtWaitStartRef.current = null;
      // Reset canvas/ticket dismissed state when new response arrives
      if (lastMsg.canvasContent) {
        setCanvasDismissed(false);
      }
      if (lastMsg.viewTicketId) {
        setTicketDismissed(false);
        setReviewPolling(true);
      }
    }
    // Detect pending assistant message ID for lightweight polling
    if (waitingForAI && dataChanged && !pendingMessageId) {
      const pendingMsg = chatData.messages.find(
        (m) => m.role === "assistant" && (m.status === "pending" || !m.content),
      );
      if (pendingMsg?._id) {
        setPendingMessageId(pendingMsg._id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, waitingForAI]);

  // Detect the pending assistant message ID for lightweight streaming poll
  useEffect(() => {
    if (!waitingForAI || !chatData?.messages || pendingMessageId) return;
    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      (lastMsg.status === "pending" || !lastMsg.content)
    ) {
      setPendingMessageId(lastMsg._id);
    }
  }, [chatData?.messages, waitingForAI, pendingMessageId]);

  // Track whether the server has a pending assistant message we didn't create.
  // Applies when waitingForAI is false — covers the post-review summary
  // placeholder that appears after the review finishes without a user prompt.
  useEffect(() => {
    if (waitingForAI) {
      setServerHasPendingMessage(false);
      return;
    }
    setServerHasPendingMessage(
      isPostReviewSummaryPending(chatData?.messages ?? []),
    );
  }, [chatData?.messages, waitingForAI]);

  // Merge lightweight streaming state into localMessages for faster UI updates
  useEffect(() => {
    if (!streamingState) return;

    // Drive canvasPolling: start when backend signals canvas is generating,
    // stop when canvasLoading clears (canvas complete or errored).
    if (streamingState.canvasLoading && !canvasPolling) {
      setCanvasPolling(true);
    } else if (!streamingState.canvasLoading && canvasPolling) {
      setCanvasPolling(false);
    }

    // After the text response is done, pendingMessageId is null but we still
    // need to apply canvas updates — use lastPendingIdRef for that phase.
    const activeId = pendingMessageId ?? lastPendingIdRef.current;
    if (!activeId) return;

    // Instrumentation: time-to-first-response
    if (
      !ttfrFiredRef.current &&
      messageSentAtRef.current &&
      (streamingState.streamingContent || streamingState.content)
    ) {
      const ttfrMs = Date.now() - messageSentAtRef.current;
      analytics.capture("ask_puntt_time_to_first_response", {
        chat_id: chatId,
        ttfr_ms: ttfrMs,
        workflow_type: chatData?.chat?.workflowType,
      });
      ttfrFiredRef.current = true;
    }

    setLocalMessages((prev) =>
      prev.map((msg) => {
        if (msg._id !== activeId) return msg;
        // Only write fields the streaming payload actually provides.
        // Unconditionally overwriting `canvasContent`/`canvasLoading` with
        // `undefined` was causing the right-pane canvas to unmount and
        // re-mount on every poll tick (visible as a full-pane flicker while
        // the user types follow-up instructions).
        const patch: Partial<AskPunttMessageExt> = {};
        if (streamingState.status !== undefined)
          patch.status = streamingState.status as AskPunttMessage["status"];
        if (streamingState.streamingContent !== undefined)
          patch.streamingContent = streamingState.streamingContent;
        if (streamingState.canvasLoading !== undefined)
          patch.canvasLoading = streamingState.canvasLoading;
        if (streamingState.suggestedResponses !== undefined)
          patch.suggestedResponses = streamingState.suggestedResponses;
        if (streamingState.content) patch.content = streamingState.content;
        if (streamingState.agentSteps?.length)
          patch.agentSteps =
            streamingState.agentSteps as AskPunttMessage["agentSteps"];
        if (streamingState.searchProgress?.length)
          patch.searchProgress =
            streamingState.searchProgress as AskPunttMessage["searchProgress"];
        if (streamingState.canvasContent)
          patch.canvasContent =
            streamingState.canvasContent as AskPunttMessage["canvasContent"];
        if (streamingState.canvasChangedSectionIds?.length)
          patch.canvasChangedSectionIds =
            streamingState.canvasChangedSectionIds;
        if (streamingState.viewTicketId)
          patch.viewTicketId = streamingState.viewTicketId;
        if (streamingState.projectData)
          patch.projectData =
            streamingState.projectData as AskPunttMessage["projectData"];
        if (streamingState.projectLoading !== undefined)
          patch.projectLoading = streamingState.projectLoading;
        if (streamingState.contentBlocks)
          patch.contentBlocks =
            streamingState.contentBlocks as AskPunttMessage["contentBlocks"];
        return { ...msg, ...patch };
      }),
    );
  }, [streamingState, pendingMessageId, canvasPolling]);

  // Keep workflowType ref in sync so the unmount cleanup reads fresh data
  useEffect(() => {
    workflowTypeRef.current = chatData?.chat?.workflowType;
  }, [chatData?.chat?.workflowType]);

  useEffect(() => {
    analytics.capture("ask_puntt_chat_viewed", { chat_id: chatId });

    // Instrumentation: session depth on unmount
    return () => {
      const durationMs = Date.now() - sessionStartRef.current;
      analytics.capture("ask_puntt_session_ended", {
        chat_id: chatId,
        duration_ms: durationMs,
        message_count: messageCountRef.current,
        workflow_type: workflowTypeRef.current,
      });
    };
  }, [chatId]);

  // Detect the latest canvas content from messages
  const activeCanvas = useMemo((): {
    canvas: CanvasContent & { changedSectionIds?: string[] };
    skill?: string;
  } | null => {
    if (canvasDismissed) return null;
    // Find the last assistant message with canvasContent
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return {
          canvas: {
            ...(msg.canvasContent as CanvasContent),
            changedSectionIds: msg.canvasChangedSectionIds,
          },
          skill: msg.activeSkill ?? undefined,
        };
      }
    }
    return null;
  }, [localMessages, canvasDismissed]);

  // Instrumentation: canvas rendered (tracked per messageId to avoid duplicate events)
  const canvasTrackedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!activeCanvas) return;
    // Find the message ID for the current active canvas
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        if (!canvasTrackedRef.current.has(msg._id)) {
          analytics.capture("ask_puntt_canvas_rendered", {
            chat_id: chatId,
            output_type: activeCanvas.canvas.outputType,
            block_count: activeCanvas.canvas.blocks?.length ?? 0,
            skill: activeCanvas.skill,
          });
          canvasTrackedRef.current.add(msg._id);
        }
        break;
      }
    }
  }, [activeCanvas, chatId, localMessages]);

  // Check if any message has canvas content (regardless of dismissed state)
  const hasCanvasContent = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return true;
      }
    }
    return false;
  }, [localMessages]);

  // Detect canvas loading state (agent is generating canvas content).
  // Not gated on waitingForAI — canvas can still be generating after the text
  // response is saved, so we rely on canvasPolling for the post-text phase.
  const isCanvasLoading = useMemo(() => {
    const lastMsg = localMessages[localMessages.length - 1];
    return !!(
      lastMsg?.role === "assistant" &&
      (lastMsg.canvasLoading || canvasPolling)
    );
  }, [localMessages, canvasPolling]);

  // Detect project loading state (agent is generating project data)
  const isProjectLoading = useMemo(() => {
    if (!waitingForAI) return false;
    const lastMsg = localMessages[localMessages.length - 1];
    return !!(lastMsg?.role === "assistant" && lastMsg.projectLoading);
  }, [localMessages, waitingForAI]);

  // Collect all viewTicketIds in chronological order (de-duplicated). Drives
  // the "Review N of M" navigation in the right pane so users can revisit
  // earlier reviews when multiple sequential reviews exist in one chat.
  const allReviewTicketIds = useMemo(
    () => collectReviewTicketIds(localMessages),
    [localMessages],
  );

  const hasViewTicketId = allReviewTicketIds.length > 0;

  const [selectedReviewIndex, setSelectedReviewIndex] = useState(-1);

  // Sticky auto-advance: jump to the newest review only when the user is
  // already on the latest one (or has no selection yet). Preserves manual
  // navigation when the user has gone back to an earlier review.
  useEffect(() => {
    if (allReviewTicketIds.length === 0) return;
    setSelectedReviewIndex((prev) => {
      if (prev < 0 || prev === allReviewTicketIds.length - 1) {
        return allReviewTicketIds.length - 1;
      }
      return prev;
    });
  }, [allReviewTicketIds.length]);

  const activeViewTicketId = useMemo((): string | null => {
    if (ticketDismissed) return null;
    if (
      selectedReviewIndex >= 0 &&
      selectedReviewIndex < allReviewTicketIds.length
    ) {
      return allReviewTicketIds[selectedReviewIndex];
    }
    return null;
  }, [ticketDismissed, selectedReviewIndex, allReviewTicketIds]);

  const handlePrevReview = useCallback(() => {
    setSelectedReviewIndex((i) => Math.max(0, i - 1));
  }, []);

  const handleNextReview = useCallback(() => {
    setSelectedReviewIndex((i) =>
      Math.min(allReviewTicketIds.length - 1, i + 1),
    );
  }, [allReviewTicketIds.length]);

  // LLM-provided pane labels for 3-panel layout (e.g. "Brief" / "Deliverable")
  const activePaneLabels = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.paneLabels) return msg.paneLabels;
    }
    return undefined;
  }, [localMessages]);

  // Detect project data from chat-level or latest message
  const activeProjectData = useMemo((): AskPunttProjectData | null => {
    const chatLevel = chatData?.chat?.projectData as
      | AskPunttProjectData
      | null
      | undefined;
    if (chatLevel) return chatLevel;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.projectData) {
        return msg.projectData as AskPunttProjectData;
      }
    }
    return null;
  }, [chatData?.chat?.projectData, localMessages]);

  // For project chats: pool the canonical review checks from each ticket so
  // the ReviewChecklist rotates through items across all parallel reviews.
  // Items are interleaved (round-robin) across tickets so reveals feel
  // parallel rather than ticket-by-ticket.
  const projectChecklistItems = useMemo(() => {
    if (!activeProjectData) return undefined;
    if (chatData?.chat?.reviewChecklist) return undefined;
    const ticketIds = activeProjectData.ticketIds ?? [];
    if (ticketIds.length === 0) return undefined;

    const titleMap = new Map<string, string>();
    for (const msg of localMessages) {
      for (const sp of msg.searchProgress ?? []) {
        if (sp.source === "Create Ticket") {
          for (const r of (sp.results ?? []) as {
            id?: string;
            title?: string;
          }[]) {
            if (r.id && r.title) titleMap.set(r.id, r.title);
          }
        }
      }
    }

    const checks = TICKET_REVIEW_CHECKS;
    const interleaved: { label: string; sub: string; ticketLabel: string }[] =
      [];
    for (let checkIdx = 0; checkIdx < checks.length; checkIdx++) {
      for (const ticketId of ticketIds) {
        const ticketLabel = titleMap.get(ticketId) ?? "Ticket";
        interleaved.push({
          ticketLabel,
          label: checks[checkIdx].label,
          sub: checks[checkIdx].sub,
        });
      }
    }
    return interleaved;
  }, [activeProjectData, localMessages, chatData?.chat?.reviewChecklist]);

  // Only treat the project review as "parallel" when there are at least 2
  // tickets — otherwise the checklist would render "Reviewing 1 tickets in
  // parallel" and switch to parallel-mode semantics for what is effectively a
  // single review.
  const projectTicketCount = activeProjectData?.ticketIds?.length ?? 0;
  const projectParallelTicketCount =
    projectTicketCount > 1 ? projectTicketCount : 0;

  // Track which ticket the user drilled into from the project view
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  // When the user drills into a project ticket, use that ID for data fetching.
  // For project chats, only show a ticket when explicitly selected (drill-in);
  // otherwise the project overview is the default landing view.
  const effectiveViewTicketId = activeProjectData
    ? selectedTicketId
    : activeViewTicketId;

  // Lift ticket data to parent to determine layout mode
  const {
    comments: viewComments,
    isLoading: viewTicketLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  } = useTicketViewData(effectiveViewTicketId, {
    pollingEnabled: reviewPolling,
  });

  // Auto-select the first comment when comments arrive and none is selected
  useEffect(() => {
    if (!selectedCommentId && viewComments?.length) {
      setSelectedCommentId(viewComments[0]._id ?? null);
    }
  }, [selectedCommentId, viewComments]);

  const isProjectChat = chatData?.chat?.workflowType === "project";

  // Theatrical typewriter reveal for the Vitalora killer-flow demo
  // response. The hook self-gates by inspecting the latest assistant
  // message's shape (`isKillerFlowAssistantMessage`), so we only
  // need a coarse tenant-level enable flag here — production tenants
  // never land a killer-flow-shaped payload, so the hook is a no-op
  // for them in practice. We keep this gated on MG/demo as a safety
  // net so an accidental killer-flow payload on a real tenant doesn't
  // surprise anyone with a 90s typewriter.
  const killerStream = useKillerFlowStream({
    enabled: isMeaningfulGigsOrDemo(),
    messages: localMessages,
  });
  const isKillerFlowStreaming = killerStream.isStreaming;

  // Theatrical reveal contract for the Vitalora killer-flow demo:
  // during the ~88s typewriter the chat plays solo (stage 1) and the
  // right-hand `ProjectStatusPane` (and its banner) only fade in once
  // streaming has completed (stage 2). Returning `null` here short-
  // circuits the JSX gate below; `activeProjectData` is restored as
  // soon as the killer-flow hook finishes its schedule.
  const displayProjectData = useMemo((): AskPunttProjectData | null => {
    if (!activeProjectData) return null;
    if (isKillerFlowStreaming) return null;
    return activeProjectData;
  }, [activeProjectData, isKillerFlowStreaming]);

  const {
    scrollContainerRef,
    sentinelRef: messagesEndRef,
    contentRef: messagesContentRef,
    handleScroll: handleMessagesScroll,
    resetScroll: resetAutoScroll,
  } = useAutoScroll(
    [
      localMessages,
      waitingForAI,
      streamingState,
      // Killer-flow typewriter tick signals — the typewriter overlays
      // content on a message whose stored ref doesn't change per frame,
      // so without these the hook would only fire once and the chat
      // would freeze at the top while text streams in below the fold.
      killerStream.activeMessageId,
      killerStream.revealedContentBlocks,
      killerStream.revealedAgentStepCount,
      killerStream.isStreaming,
    ],
    // resetKey — switching to a new chat clears the sticky-up state so
    // the layout effect re-pins to the bottom of the new chat instead
    // of inheriting the previous chat's scroll position.
    chatId,
  );

  // Enable 3-pane (brief + artwork + comments) when viewing a ticket with
  // context assets. In project chats this fires only when the user has
  // drilled into a specific ticket (effectiveViewTicketId falls back to
  // selectedTicketId when activeProjectData is set, see above), so the
  // project overview remains the default landing view.
  const use3PaneLayout = !!effectiveViewTicketId && hasContextAssets;

  const has2PaneRight =
    !use3PaneLayout &&
    !!(
      effectiveViewTicketId ||
      activeCanvas ||
      isProjectLoading ||
      activeProjectData
    );

  // Reset selected ticket when navigating to a different chat
  useEffect(() => {
    setSelectedTicketId(null);
  }, [chatId]);

  // Reset selected comment when switching between project tickets
  useEffect(() => {
    setSelectedCommentId(null);
  }, [selectedTicketId]);

  const [labelSheetCollapsed, setLabelSheetCollapsed] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [artworkCollapsed, setArtworkCollapsed] = useState(false);

  // When entering 3-column layout, collapse the app sidebar (if open) to free horizontal space
  const prev3Pane = useRef(false);
  useEffect(() => {
    if (use3PaneLayout && !prev3Pane.current && sidebarOpen) {
      dispatch(setDrawerOpen({ app: false }));
    }
    prev3Pane.current = use3PaneLayout;
  }, [use3PaneLayout, sidebarOpen, dispatch]);

  const handleDismissCanvas = useCallback(() => {
    setCanvasDismissed(true);
  }, []);

  const handleSectionClick = useCallback((sectionId: string, title: string) => {
    setSectionContext({ id: sectionId, title });
  }, []);

  const handleProjectDataUpdate = useCallback(
    (updatedData: AskPunttProjectData) => {
      queryClient.setQueryData(
        askPunttKeys.messages(chatId),
        (old: typeof chatData) => {
          if (!old?.chat) return old;
          return {
            ...old,
            chat: { ...old.chat, projectData: updatedData },
          };
        },
      );
    },
    [chatId, queryClient],
  );

  const labelSheetRef = useRef<AssetViewerPaneHandle>(null);
  const artworkPaneRef = useRef<AssetViewerPaneHandle>(null);
  const questionFormRef = useRef<QuestionFormHandle>(null);
  const [_questionHasAnswer, setQuestionHasAnswer] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestionHasAnswer, setCurrentQuestionHasAnswer] =
    useState(false);
  const questionUploadInputRef = useRef<HTMLInputElement>(null);
  const [contextHighlight, setContextHighlight] = useState<{
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const contextHighlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const handleCitationClick = useCallback(
    (
      pageNumber: number,
      position?: { x: number; y: number },
      sourceUrl?: string,
      originalURL?: string,
    ) => {
      // Switch context pane to the file the citation points to. Signed URLs
      // carry ephemeral query-string signatures, so match on pathname only,
      // with originalURL as a fallback for files that carry an unsigned key.
      const targetIdx = findContextFileIndex(
        contextFiles,
        sourceUrl,
        originalURL,
      );
      if (targetIdx >= 0) {
        // Pass pageNumber along so it lands on the newly-mounted viewer even
        // when citations have no rel_x/rel_y to drive the highlight effect.
        labelSheetRef.current?.navigateToFile(targetIdx, pageNumber);
      } else {
        labelSheetRef.current?.navigateToPage(pageNumber);
      }
      if (position) {
        if (contextHighlightTimerRef.current)
          clearTimeout(contextHighlightTimerRef.current);
        // Clear first to restart the animation when clicking the same source again
        setContextHighlight(null);
        requestAnimationFrame(() => {
          setContextHighlight({
            x: position.x,
            y: position.y,
            page: pageNumber,
          });
          contextHighlightTimerRef.current = setTimeout(
            () => setContextHighlight(null),
            3000,
          );
        });
      }
    },
    [contextFiles],
  );

  useEffect(() => {
    return () => {
      if (contextHighlightTimerRef.current) {
        clearTimeout(contextHighlightTimerRef.current);
      }
    };
  }, []);

  const handleCommentClick = useCallback(
    (comment: {
      _id?: string;
      rel_x?: number;
      rel_y?: number;
      page_num?: number;
    }) => {
      setSelectedCommentId(comment._id ?? null);
      if (comment.rel_x != null && comment.rel_y != null) {
        if (focusTimerRef.current) clearTimeout(focusTimerRef.current);
        setHighlightPing({
          x: comment.rel_x,
          y: comment.rel_y,
          page: comment.page_num,
        });
        focusTimerRef.current = setTimeout(() => setHighlightPing(null), 3000);
      }
    },
    [],
  );

  const pinDragMutation = useMutation({ mutationFn: editComment });

  const handlePinDragEnd = useCallback(
    (comment: BaseTicketComment, newRelX: number, newRelY: number) => {
      if (!activeViewTicketId || !comment._id) return;

      // Optimistic update: immediately reflect new position in cache
      queryClient.setQueryData<BaseTicketComment[]>(
        ["ticket-view-comments", activeViewTicketId],
        (old) =>
          old?.map((c) =>
            c._id === comment._id
              ? { ...c, rel_x: newRelX, rel_y: newRelY }
              : c,
          ),
      );

      pinDragMutation.mutate(
        {
          ticketId: activeViewTicketId,
          messageId: comment._id,
          payload: {
            description: comment.description ?? "",
            rel_x: newRelX,
            rel_y: newRelY,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["ticket-view-comments", activeViewTicketId],
            });
          },
          onError: () => {
            // Revert on failure
            queryClient.invalidateQueries({
              queryKey: ["ticket-view-comments", activeViewTicketId],
            });
          },
        },
      );
    },
    [activeViewTicketId, pinDragMutation, queryClient],
  );

  const handleStop = async () => {
    const msgId = pendingMessageId;
    // Optimistic: re-enable input immediately
    setWaitingForAI(false);
    setPendingMessageId(null);

    if (!msgId) return;

    try {
      await abortMessage.mutateAsync(msgId);
    } catch {
      // Revert: restore polling so the message can complete naturally
      setWaitingForAI(true);
      setPendingMessageId(msgId);
    }
  };

  const handleSend = async (
    overrideMessage?: string,
    extraAttachments?: {
      key: string;
      source: string;
      type: "image" | "video" | "file";
      name?: string;
    }[],
  ) => {
    const text = overrideMessage ?? inputMessage;
    const allAttachments = [...readyAttachments, ...(extraAttachments ?? [])];
    if (
      (!text.trim() && allAttachments.length === 0) ||
      sendMessage.isPending ||
      hasPending
    )
      return;
    resetAutoScroll();

    const messageToSend = text;
    const attachments = allAttachments;
    const messageLength = messageToSend.length;
    const messageIndex = localMessages.filter((m) => m.role === "user").length;

    const userMsg: AskPunttMessage = {
      _id: `local-user-${Date.now()}`,
      chatId,
      role: "user",
      content: messageToSend,
      attachments: attachments.length > 0 ? attachments : undefined,
      createdAt: new Date().toISOString(),
    };

    const contextToSend = sectionContext;

    setLocalMessages((prev) => [...prev, userMsg]);
    setInputMessage("");
    setSectionContext(null);
    clearAll();

    // Instrumentation: mark send time for TTFR measurement
    messageSentAtRef.current = Date.now();
    ttfrFiredRef.current = false;

    try {
      const result = await sendMessage.mutateAsync({
        message: messageToSend,
        attachments: attachments.length > 0 ? attachments : undefined,
        sectionContext: contextToSend ?? undefined,
      });

      messageCountRef.current += 1;

      // Replace the optimistic user message with the server-confirmed one
      setLocalMessages((prev) => {
        const withoutOptimistic = prev.filter((m) => m._id !== userMsg._id);
        return [...withoutOptimistic, result.userMessage];
      });
      knownMessageCountRef.current = localMessages.length;
      dataAtWaitStartRef.current = dataUpdatedAt;
      lastPendingIdRef.current = null;
      setWaitingForAI(true);

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_sent", {
        chat_id: chatId,
        message_length: messageLength,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        message_index: messageIndex,
        workflow_id: result.workflowId,
      });
    } catch (error) {
      setLocalMessages((prev) => prev.filter((m) => m._id !== userMsg._id));

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_send_error", {
        chat_id: chatId,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to send message:", error);
    }
  };

  const chat = chatData?.chat;
  const serverReviewStatus = chat?.reviewStatus ?? null;

  // For project chats, the overall review is still pending until findingsSummary
  // arrives — serverReviewStatus is cleared per-ticket so it oscillates to null
  // between parallel ticket reviews. The killer-flow demo backs its `review_started`
  // phase with the typewriter window, so treat that as pending too so the
  // "Review complete" transition toast fires when the reveal finishes.
  const projectReviewPending =
    !!activeProjectData &&
    (activeProjectData.ticketIds?.length ?? 0) > 0 &&
    (!activeProjectData.findingsSummary || isKillerFlowStreaming);

  // Show a transient "Review complete" when the review finishes. For project
  // chats, "reviewing" stays true until findingsSummary arrives, so the toast
  // fires once at the end instead of on every per-ticket transition.
  const isReviewing = !!serverReviewStatus || projectReviewPending;
  const [completionStatus, setCompletionStatus] = useState<string | null>(null);
  const wasReviewingRef = useRef(false);
  useEffect(() => {
    const wasReviewing = wasReviewingRef.current;
    wasReviewingRef.current = isReviewing;
    if (wasReviewing && !isReviewing) {
      setCompletionStatus("Review complete");
      const timer = setTimeout(() => setCompletionStatus(null), 5000);
      return () => clearTimeout(timer);
    }
    if (isReviewing) {
      setCompletionStatus(null);
    }
  }, [isReviewing]);

  const reviewStatus = serverReviewStatus ?? completionStatus;

  // Sync review polling state with reviewStatus from server.
  useEffect(() => {
    setReviewPolling(!!serverReviewStatus || projectReviewPending);
  }, [serverReviewStatus, projectReviewPending]);

  // When review polling stops, force a final fetch to pick up any comments
  // saved in the window between the last poll and polling stopping. Also
  // re-invalidate the chat so the frontend catches a new reviewStatus that
  // the backend may have set for a post-review summary (the gap between the
  // review clearing reviewStatus and the summary setting it is ~50ms; the
  // invalidation fires a refetch that catches any new status in that window).
  const prevReviewPollingRef = useRef(false);
  useEffect(() => {
    if (prevReviewPollingRef.current && !reviewPolling && activeViewTicketId) {
      queryClient.invalidateQueries({
        queryKey: ["ticket-view", activeViewTicketId],
      });
      queryClient.invalidateQueries({
        queryKey: ["ticket-view-comments", activeViewTicketId],
      });
      queryClient.invalidateQueries({
        queryKey: askPunttKeys.messages(chatId),
      });
    }
    prevReviewPollingRef.current = reviewPolling;
  }, [reviewPolling, activeViewTicketId, queryClient, chatId]);

  // Find the active skill label from the last assistant message
  const activeSkillLabel = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.activeSkill) {
        return SKILL_LABELS[msg.activeSkill];
      }
    }
    return undefined;
  }, [localMessages]);

  // Extract suggested responses from the last assistant message
  const activeSuggestedResponses = useMemo(() => {
    const lastMsg = localMessages[localMessages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.suggestedResponses &&
      lastMsg.suggestedResponses.length > 0
    ) {
      return lastMsg.suggestedResponses;
    }
    return null;
  }, [localMessages]);

  // Hide suggestion pills when the last message has userQuestions (the form handles it)
  const lastMessage = localMessages[localMessages.length - 1];
  const hasQuestionForm =
    lastMessage?.role === "assistant" &&
    lastMessage.userQuestions &&
    lastMessage.userQuestions.length > 0;

  // Reset answer state when a new question form appears
  useEffect(() => {
    if (hasQuestionForm) {
      setQuestionHasAnswer(false);
      setCurrentQuestionHasAnswer(false);
      setCurrentQuestionIndex(0);
    }
  }, [hasQuestionForm]);

  const totalQuestions = hasQuestionForm
    ? (lastMessage?.userQuestions?.length ?? 0)
    : 0;

  const handleQuestionNext = useCallback(() => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setCurrentQuestionHasAnswer(false);
    } else {
      questionFormRef.current?.submit();
    }
  }, [currentQuestionIndex, totalQuestions]);

  const handleQuestionBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  }, [currentQuestionIndex]);

  const handleUploadRequest = useCallback(() => {
    questionUploadInputRef.current?.click();
  }, []);
  const showSuggestions =
    activeSuggestedResponses &&
    !waitingForAI &&
    !sendMessage.isPending &&
    !hasQuestionForm;

  // When the review is complete, find the last review-status message so the
  // ReviewChecklist can be anchored inline at that position rather than always
  // floating to the bottom as new messages arrive.
  const reviewChecklistComplete =
    !!chatData?.chat?.reviewChecklist && !reviewPolling;
  const reviewChecklistAnchorIdx = reviewChecklistComplete
    ? localMessages.reduce(
        (last, m, i) => (isReviewStatusMessage(m) ? i : last),
        -1,
      )
    : -1;

  return (
    <AskPunttDropZone
      onFilesDropped={addFiles}
      className="flex h-full flex-col overflow-hidden"
      data-testid="ask-puntt-chat"
    >
      {/* Breadcrumb */}
      <PageHeader>
        <Link to={askPunttRoute.to} className="puntt-link">
          <Text
            size="2"
            weight="medium"
            className="text-puntt-accent-11 hover:underline"
          >
            Ask Puntt
          </Text>
        </Link>
        {chat?.title && (
          <>
            <CaretRight size={12} className="text-puntt-neutral-gray-9" />
            <Text
              size="2"
              weight="medium"
              className="max-w-[400px] truncate text-puntt-neutral-gray-11"
            >
              {chat.title}
            </Text>
          </>
        )}
        {chat?.ownerName && (
          <Badge
            size="1"
            variant="soft"
            color="gray"
            className="ml-2"
            data-testid="ask-puntt-chat-owner-badge"
          >
            owned by {chat.ownerName}
          </Badge>
        )}
        {activeSkillLabel && (
          <Badge size="1" variant="soft" color="blue" className="ml-2">
            {activeSkillLabel}
          </Badge>
        )}
        {ticketDismissed && hasViewTicketId && (
          <Button
            data-testid="chat-button-1170"
            size="1"
            variant="soft"
            color="blue"
            className="ml-auto cursor-pointer"
            onClick={() => setTicketDismissed(false)}
          >
            <ArrowsOutSimple size={14} />
            <Text size="1">Show Review</Text>
          </Button>
        )}
        {canvasDismissed && hasCanvasContent && (
          <Button
            data-testid="chat-button-1182"
            size="1"
            variant="soft"
            color="blue"
            className={`cursor-pointer ${!(ticketDismissed && hasViewTicketId) ? "ml-auto" : ""}`}
            onClick={() => setCanvasDismissed(false)}
          >
            <ArrowsOutSimple size={14} />
            <Text size="1">Show Canvas</Text>
          </Button>
        )}
      </PageHeader>

      {/* Main content: multi-pane layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Chat Pane */}
        <div
          style={
            has2PaneRight || use3PaneLayout
              ? {
                  flexGrow: chatCollapsed ? 0 : 1,
                  flexShrink: 1,
                  flexBasis: "0%",
                  minWidth: 0,
                  transition:
                    "flex-grow 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1)",
                }
              : undefined
          }
          className={`relative flex flex-col overflow-hidden ${
            !(has2PaneRight || use3PaneLayout)
              ? "w-full"
              : chatCollapsed
                ? "pointer-events-none opacity-0"
                : "border-r border-puntt-neutral-gray-6 opacity-100"
          }`}
        >
          {(use3PaneLayout || has2PaneRight) && !chatCollapsed && (
            <div className="flex h-10 shrink-0 items-center justify-end border-b border-puntt-neutral-gray-6 px-4">
              <Tooltip content="Collapse chat pane">
                <IconButton
                  size="1"
                  variant="ghost"
                  color="gray"
                  aria-label="Collapse chat pane"
                  data-testid="pane-collapse-chat"
                  onClick={() => setChatCollapsed(true)}
                >
                  <ArrowsInSimple size={14} />
                </IconButton>
              </Tooltip>
            </div>
          )}
          {/* Messages */}
          <div
            ref={scrollContainerRef}
            onScroll={handleMessagesScroll}
            className="flex-1 overflow-y-auto px-4 py-6"
            data-testid="ask-puntt-messages"
          >
            <div
              ref={messagesContentRef}
              className="mx-auto max-w-[720px] space-y-4"
            >
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-16 animate-pulse rounded-lg bg-puntt-neutral-gray-4"
                    />
                  ))}
                </div>
              ) : (
                <>
                  {localMessages.map((message, idx) => {
                    const isLast = idx === localMessages.length - 1;
                    const isLastAssistant =
                      message.role === "assistant" && isLast && !waitingForAI;
                    const precedingUserHasAttachments =
                      isLast &&
                      message.role === "assistant" &&
                      waitingForAI &&
                      !!localMessages[idx - 1]?.attachments?.length;

                    // Hide per-ticket "review underway" messages when the
                    // ReviewChecklist is active so the checklist is the
                    // single source of review progress. The post-map
                    // ReviewChecklist replaces the last one. Don't hide a
                    // mid-stream message that happens to match the shape.
                    const isReviewStatusMsg = isReviewStatusMessage(message);
                    const reviewChecklistActive =
                      reviewPolling || !!chatData?.chat?.reviewChecklist;
                    if (
                      isReviewStatusMsg &&
                      reviewChecklistActive &&
                      (!isLast || !waitingForAI)
                    ) {
                      // Inline anchor: once the review is complete, render the
                      // ReviewChecklist at the last review-status message's
                      // position so it stays fixed in the chat as new messages
                      // arrive below it.
                      if (idx === reviewChecklistAnchorIdx) {
                        return (
                          <ReviewChecklist
                            key={message._id}
                            workflowType={chatData?.chat?.workflowType ?? ""}
                            isReviewPolling={false}
                            reviewComplete={true}
                            dynamicItems={
                              chatData?.chat?.reviewChecklist ??
                              projectChecklistItems
                            }
                            parallelTicketCount={undefined}
                          />
                        );
                      }
                      return null;
                    }

                    // Theatrical typewriter override for the killer flow:
                    // when this is the message currently being paced,
                    // swap its `contentBlocks` and slice its `agentSteps`
                    // so ChatMessage renders the partial reveal. Once
                    // streaming finishes, `revealedContentBlocks` is null
                    // and the message renders normally.
                    const isStreamingTarget =
                      killerStream.activeMessageId === message._id &&
                      killerStream.revealedContentBlocks !== null;
                    const displayedMessage = isStreamingTarget
                      ? {
                          ...message,
                          contentBlocks:
                            killerStream.revealedContentBlocks ?? undefined,
                          agentSteps:
                            killerStream.revealedAgentStepCount != null
                              ? (message.agentSteps ?? []).slice(
                                  0,
                                  killerStream.revealedAgentStepCount,
                                )
                              : message.agentSteps,
                        }
                      : message;

                    return (
                      <ChatMessage
                        key={message._id}
                        message={displayedMessage}
                        chatId={chatId}
                        reviewFiles={latestReviewFiles}
                        localPreviewUrls={localPreviewUrls}
                        isStreaming={
                          (waitingForAI &&
                            isLast &&
                            message.role === "assistant") ||
                          isStreamingTarget
                        }
                        isLastAssistant={isLastAssistant && !isStreamingTarget}
                        onSubmitAnswers={handleSend}
                        formDisabled={sendMessage.isPending || waitingForAI}
                        questionFormRef={
                          isLast && hasQuestionForm
                            ? questionFormRef
                            : undefined
                        }
                        onAnswerChange={
                          isLast && hasQuestionForm
                            ? setQuestionHasAnswer
                            : undefined
                        }
                        hideInlineQuestionForm={!!(isLast && hasQuestionForm)}
                        precedingUserHasAttachments={
                          precedingUserHasAttachments
                        }
                      />
                    );
                  })}
                  {/* Single, stable-position ReviewChecklist render. Lives
                      outside the messages map so its internal stagger/timer
                      state survives new review-status messages arriving from
                      the project review workflow. */}
                  {(() => {
                    const lastMsg = localMessages[localMessages.length - 1];
                    const lastIsAssistant = lastMsg?.role === "assistant";
                    const hasReviewChecklist =
                      !!chatData?.chat?.reviewChecklist;
                    const hasCreateTicket = localMessages.some((m) =>
                      m.agentSteps?.some(
                        (s) =>
                          s.type === "tool_call" && s.tool === "create_ticket",
                      ),
                    );

                    // After the agent's response: checklist sits below the
                    // last assistant message while the Temporal review runs
                    // or once a saved checklist exists. For the killer
                    // flow the response lands instantly but we keep the
                    // checklist on screen during the theatrical reveal so
                    // the prospect sees the per-ticket checkmarks staggering
                    // through while the assistant "writes up" findings.
                    // Skip rendering once the checklist is anchored inline
                    // and complete inside the messages map (master fix from
                    // PR #3425) — that guard naturally also hides the
                    // killer-flow checklist once its stagger lands, so
                    // both conditions compose cleanly.
                    const showAfterResponse =
                      !waitingForAI &&
                      lastIsAssistant &&
                      (reviewPolling ||
                        hasReviewChecklist ||
                        isKillerFlowStreaming) &&
                      !reviewChecklistComplete;

                    // While waiting: show as soon as create_ticket fires in
                    // agent steps (before reviewChecklist is saved). Skip when
                    // the completed checklist is already anchored inline.
                    const showWhileWaiting =
                      waitingForAI &&
                      !lastIsAssistant &&
                      (reviewPolling ||
                        hasReviewChecklist ||
                        hasCreateTicket) &&
                      !reviewChecklistComplete;

                    if (showAfterResponse || showWhileWaiting) {
                      return (
                        <ReviewChecklist
                          workflowType={chatData?.chat?.workflowType ?? ""}
                          isReviewPolling={reviewPolling}
                          reviewComplete={
                            showAfterResponse &&
                            !reviewPolling &&
                            hasReviewChecklist
                          }
                          dynamicItems={
                            chatData?.chat?.reviewChecklist ??
                            projectChecklistItems
                          }
                          parallelTicketCount={
                            hasReviewChecklist
                              ? undefined
                              : projectParallelTicketCount || undefined
                          }
                        />
                      );
                    }

                    if (waitingForAI && !lastIsAssistant) {
                      return (
                        <ThinkingState
                          precedingUserHasAttachments={
                            !!localMessages[localMessages.length - 1]
                              ?.attachments?.length
                          }
                        />
                      );
                    }

                    return null;
                  })()}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="shrink-0 border-t border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-4">
            <div className="mx-auto max-w-[720px] space-y-3">
              {displayProjectData && (
                <ProjectReviewStatusBanner projectData={displayProjectData} />
              )}
              <div
                {...dropZoneProps}
                className={`relative overflow-hidden rounded-2xl border bg-base-white shadow-sm transition-shadow duration-200 focus-within:border-puntt-accent-7 focus-within:shadow-md focus-within:ring-2 focus-within:ring-puntt-accent-6/20 ${isDragOver ? "border-puntt-accent-9 ring-2 ring-puntt-accent-6/40" : "border-puntt-neutral-gray-6"}`}
                data-testid="ask-puntt-chat-drop-zone"
              >
                {isDragOver && (
                  <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center rounded-2xl bg-puntt-accent-2/80 backdrop-blur-sm">
                    <Upload
                      size={28}
                      weight="duotone"
                      className="mb-1 text-puntt-accent-9"
                    />
                    <Text
                      size="2"
                      weight="medium"
                      className="text-puntt-accent-11"
                    >
                      Drop files here
                    </Text>
                  </div>
                )}

                <input
                  ref={questionUploadInputRef}
                  data-testid="ask-puntt-file-upload-input"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) addFiles(Array.from(e.target.files));
                    e.target.value = "";
                  }}
                />

                {hasQuestionForm && !waitingForAI && (
                  <div className="border-b border-puntt-neutral-gray-6 px-4 pb-2 pt-3">
                    <Flex align="center" justify="between">
                      <Text size="1" className="text-puntt-neutral-gray-9">
                        Question {currentQuestionIndex + 1} of {totalQuestions}
                      </Text>
                      <Flex gap="1">
                        {Array.from({ length: totalQuestions }).map((_, i) => (
                          <div
                            key={i}
                            className={`size-1.5 rounded-full transition-colors duration-200 ${
                              i <= currentQuestionIndex
                                ? "bg-puntt-accent-9"
                                : "bg-puntt-neutral-gray-5"
                            }`}
                          />
                        ))}
                      </Flex>
                    </Flex>
                  </div>
                )}

                {showSuggestions && (
                  <SuggestedResponses
                    suggestions={activeSuggestedResponses}
                    onSelect={(suggestion) => {
                      analytics.capture(
                        "ask_puntt_suggested_response_clicked",
                        {
                          chat_id: chatId,
                          suggestion_text: suggestion.slice(0, 100),
                          workflow_type: chatData?.chat?.workflowType,
                        },
                      );
                      handleSend(suggestion);
                    }}
                    disabled={sendMessage.isPending || waitingForAI}
                  />
                )}

                {/* Section context chip */}
                {sectionContext && (
                  <div className="flex animate-chip-slide-down items-center gap-2 overflow-hidden border-b border-puntt-accent-4 bg-puntt-accent-2 px-3 py-1.5 text-xs text-puntt-accent-11">
                    <span className="flex-1 truncate">
                      Re: <strong>{sectionContext.title}</strong>
                    </span>
                    <button
                      data-testid="chat-button-1472"
                      onClick={() => setSectionContext(null)}
                      aria-label="Remove section context"
                      className="shrink-0 cursor-pointer rounded p-0.5 hover:bg-puntt-accent-3 focus:outline-none focus:ring-1 focus:ring-puntt-accent-9"
                    >
                      ×
                    </button>
                  </div>
                )}
                {hasQuestionForm && !waitingForAI ? (
                  <div className="px-3 py-2">
                    <QuestionForm
                      data-testid="chat-question-form-1483"
                      ref={questionFormRef}
                      questions={
                        (lastMessage?.userQuestions as AskPunttUserQuestion[]) ??
                        []
                      }
                      onSubmit={(formatted, attachments) => {
                        analytics.capture("ask_puntt_question_form_submitted", {
                          chat_id: chatId,
                          question_count: totalQuestions,
                          workflow_type: chatData?.chat?.workflowType,
                        });
                        handleSend(formatted || undefined, attachments);
                      }}
                      disabled={
                        sendMessage.isPending || waitingForAI || hasPending
                      }
                      hideSubmitButton
                      visibleQuestionIndex={currentQuestionIndex}
                      onCurrentQuestionAnswerChange={
                        setCurrentQuestionHasAnswer
                      }
                      onUploadRequest={handleUploadRequest}
                      chatLevelFileCount={attachedItems.length}
                    />
                  </div>
                ) : (
                  <TextArea
                    data-testid="ask-puntt-chat-reply-input"
                    size="2"
                    variant="surface"
                    placeholder="Reply..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    onPaste={handlePaste}
                    className="min-h-[60px] w-full resize-none border-none !shadow-none"
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                )}

                {/* Attached files preview */}
                {attachedItems.length > 0 && (
                  <Flex gap="2" wrap="wrap" className="px-3 pb-1">
                    {attachedItems.map((item, idx) => (
                      <Flex
                        key={item.id}
                        align="center"
                        gap="1"
                        className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                        data-testid="ask-puntt-attached-file"
                      >
                        {item.status === "pending" && (
                          <CircleNotch
                            size={12}
                            weight="bold"
                            className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                          />
                        )}
                        <Text
                          size="1"
                          className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                        >
                          {item.file.name}
                          {item.status === "error" && " (failed)"}
                        </Text>
                        <button
                          data-testid="chat-button-1554"
                          onClick={() => removeFile(idx)}
                          className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                          aria-label={`Remove ${item.file.name}`}
                        >
                          ×
                        </button>
                      </Flex>
                    ))}
                  </Flex>
                )}

                <Flex justify="between" align="center" className="px-3 py-1.5">
                  {hasQuestionForm && !waitingForAI ? (
                    <>
                      <Button
                        data-testid="chat-button-1569"
                        size="2"
                        variant="ghost"
                        color="gray"
                        disabled={sendMessage.isPending || waitingForAI}
                        onClick={() => questionFormRef.current?.submitAny()}
                        className="cursor-pointer"
                      >
                        Just Get Started
                        <ArrowRight size={14} weight="bold" />
                      </Button>
                      <Flex gap="2" align="center">
                        <Button
                          data-testid="chat-button-1581"
                          size="2"
                          variant="soft"
                          color="gray"
                          disabled={
                            currentQuestionIndex === 0 ||
                            sendMessage.isPending ||
                            waitingForAI
                          }
                          onClick={handleQuestionBack}
                          className="cursor-pointer"
                        >
                          <ArrowLeft size={14} weight="bold" />
                          Back
                        </Button>
                        <Button
                          data-testid="question-next-btn"
                          size="2"
                          variant="solid"
                          color="blue"
                          disabled={
                            !currentQuestionHasAnswer ||
                            sendMessage.isPending ||
                            waitingForAI ||
                            hasPending
                          }
                          onClick={handleQuestionNext}
                          className="cursor-pointer"
                        >
                          {currentQuestionIndex < totalQuestions - 1
                            ? "Next"
                            : "Submit Answers"}
                          <ArrowRight size={14} weight="bold" />
                        </Button>
                      </Flex>
                    </>
                  ) : (
                    <>
                      <InputPlusMenu
                        onFilesSelected={addFiles}
                        onScreenshotCaptured={addFile}
                        enabledConnectors={connectorsArrayToRecord(
                          chat?.enabledConnectors,
                          availableConnectors,
                        )}
                        connectorsDisabled
                        availableConnectors={availableConnectors}
                      />
                      {waitingForAI ? (
                        <Button
                          data-testid="ask-puntt-chat-stop"
                          size="2"
                          variant="soft"
                          color="gray"
                          onClick={handleStop}
                          disabled={abortMessage.isPending}
                          className="cursor-pointer transition-all duration-200"
                          aria-label="Stop generating"
                        >
                          <Stop size={14} weight="fill" />
                          Stop
                        </Button>
                      ) : (
                        <Button
                          data-testid="ask-puntt-chat-send"
                          size="2"
                          variant={
                            inputMessage.trim() || readyAttachments.length > 0
                              ? "solid"
                              : "ghost"
                          }
                          color={
                            hasPending
                              ? "gray"
                              : inputMessage.trim() ||
                                  readyAttachments.length > 0
                                ? "blue"
                                : "gray"
                          }
                          disabled={
                            (!inputMessage.trim() &&
                              readyAttachments.length === 0) ||
                            sendMessage.isPending ||
                            hasPending
                          }
                          onClick={() => handleSend()}
                          className="cursor-pointer transition-all duration-200"
                          aria-label="Send message"
                        >
                          {hasPending ? (
                            <CircleNotch
                              size={14}
                              weight="bold"
                              className="animate-spin"
                            />
                          ) : (
                            <ArrowUp size={14} weight="bold" />
                          )}
                        </Button>
                      )}
                    </>
                  )}
                </Flex>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane(s) */}
        {use3PaneLayout ? (
          <>
            {/* Collapsed Chat label */}
            <button
              onClick={() => setChatCollapsed(false)}
              style={{
                width: chatCollapsed ? 32 : 0,
                opacity: chatCollapsed ? 1 : 0,
                overflow: "hidden",
                flexShrink: 0,
                pointerEvents: chatCollapsed ? "auto" : "none",
                transition:
                  "width 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1), background-color 150ms ease",
              }}
              className={`box-border flex h-full cursor-pointer items-center justify-center bg-puntt-neutral-gray-2 hover:bg-puntt-neutral-gray-3 ${
                chatCollapsed
                  ? "border-r border-puntt-neutral-gray-6"
                  : "border-0"
              }`}
              aria-label="Expand Chat pane"
              aria-hidden={!chatCollapsed}
              tabIndex={chatCollapsed ? 0 : -1}
              data-testid="pane-expand-chat"
            >
              <Text
                size="1"
                weight="medium"
                className="whitespace-nowrap uppercase tracking-wide text-puntt-neutral-gray-9"
                style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                }}
              >
                Chat
              </Text>
            </button>

            {/* Collapsed Context label */}
            <button
              onClick={() => setLabelSheetCollapsed(false)}
              style={{
                width: labelSheetCollapsed ? 32 : 0,
                opacity: labelSheetCollapsed ? 1 : 0,
                overflow: "hidden",
                flexShrink: 0,
                pointerEvents: labelSheetCollapsed ? "auto" : "none",
                transition:
                  "width 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1), background-color 150ms ease",
              }}
              className={`box-border flex h-full cursor-pointer items-center justify-center bg-puntt-neutral-gray-2 hover:bg-puntt-neutral-gray-3 ${
                labelSheetCollapsed
                  ? "border-r border-puntt-neutral-gray-6"
                  : "border-0"
              }`}
              aria-label="Expand Context pane"
              aria-hidden={!labelSheetCollapsed}
              tabIndex={labelSheetCollapsed ? 0 : -1}
              data-testid="pane-expand-context"
            >
              <Text
                size="1"
                weight="medium"
                className="whitespace-nowrap uppercase tracking-wide text-puntt-neutral-gray-9"
                style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                }}
              >
                {activePaneLabels?.context ?? "Context"} &middot;{" "}
                {contextFiles.length}{" "}
                {contextFiles.length === 1 ? "file" : "files"}
              </Text>
            </button>

            {/* Context pane */}
            <div
              data-testid="pane-context"
              style={{
                flexGrow: labelSheetCollapsed ? 0 : 1,
                flexShrink: 1,
                flexBasis: "0%",
                minWidth: 0,
                overflow: "hidden",
                transition: "flex-grow 300ms cubic-bezier(0.16,1,0.3,1)",
              }}
              className={`animate-canvas-slide-in ${
                labelSheetCollapsed
                  ? ""
                  : "border-r border-puntt-neutral-gray-6"
              }`}
            >
              <div
                className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                  labelSheetCollapsed
                    ? "translate-x-5 opacity-0"
                    : "translate-x-0 opacity-100"
                }`}
              >
                {viewTicketLoading ? (
                  <div className="flex h-full items-center justify-center bg-base-white">
                    <Text size="2" className="text-puntt-neutral-gray-9">
                      Loading...
                    </Text>
                  </div>
                ) : (
                  <AssetViewerPane
                    // Reset internal viewer state (incl. Office snapshot) per
                    // ticket so an xlsx → image transition on one ticket can't
                    // bleed into the next.
                    key={effectiveViewTicketId ?? "no-ticket"}
                    ref={labelSheetRef}
                    title={activePaneLabels?.context ?? "Context"}
                    assets={contextFiles}
                    highlightPosition={contextHighlight}
                    onCollapse={() => setLabelSheetCollapsed(true)}
                  />
                )}
              </div>
            </div>

            {/* Collapsed Artwork label */}
            <button
              onClick={() => setArtworkCollapsed(false)}
              style={{
                width: artworkCollapsed ? 32 : 0,
                opacity: artworkCollapsed ? 1 : 0,
                overflow: "hidden",
                flexShrink: 0,
                pointerEvents: artworkCollapsed ? "auto" : "none",
                transition:
                  "width 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1), background-color 150ms ease",
              }}
              className={`box-border flex h-full cursor-pointer items-center justify-center bg-puntt-neutral-gray-2 hover:bg-puntt-neutral-gray-3 ${
                artworkCollapsed
                  ? "border-r border-puntt-neutral-gray-6"
                  : "border-0"
              }`}
              aria-label="Expand Artwork pane"
              aria-hidden={!artworkCollapsed}
              tabIndex={artworkCollapsed ? 0 : -1}
              data-testid="pane-expand-artwork"
            >
              <Text
                size="1"
                weight="medium"
                className="whitespace-nowrap uppercase tracking-wide text-puntt-neutral-gray-9"
                style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                }}
              >
                {activePaneLabels?.artwork ?? "Artwork"} &middot;{" "}
                {latestReviewFiles.length}{" "}
                {latestReviewFiles.length === 1 ? "file" : "files"}
              </Text>
            </button>

            {/* Artwork pane */}
            <div
              data-testid="pane-artwork"
              style={{
                flexGrow: artworkCollapsed ? 0 : 1,
                flexShrink: 1,
                flexBasis: "0%",
                minWidth: 0,
                overflow: "hidden",
                transition: "flex-grow 300ms cubic-bezier(0.16,1,0.3,1)",
              }}
              className="animate-canvas-slide-in"
            >
              <div
                className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                  artworkCollapsed
                    ? "translate-x-5 opacity-0"
                    : "translate-x-0 opacity-100"
                }`}
              >
                {viewTicketLoading ? (
                  <div className="flex h-full items-center justify-center bg-base-white">
                    <Text size="2" className="text-puntt-neutral-gray-9">
                      Loading...
                    </Text>
                  </div>
                ) : (
                  <AssetViewerPane
                    // Reset internal viewer state (incl. Office snapshot) per
                    // ticket so an xlsx → image transition on one ticket can't
                    // bleed into the next.
                    key={effectiveViewTicketId ?? "no-ticket"}
                    ref={artworkPaneRef}
                    title={activePaneLabels?.artwork ?? "Artwork"}
                    assets={latestReviewFiles}
                    reviewStatus={reviewStatus}
                    comments={viewComments ?? []}
                    activeCommentId={selectedCommentId}
                    onCommentClick={handleCommentClick}
                    onPinClick={handleCommentClick}
                    onPinDragEnd={handlePinDragEnd}
                    onCitationClick={handleCitationClick}
                    onCollapse={() => setArtworkCollapsed(true)}
                    highlightPosition={highlightPing}
                    ticketId={effectiveViewTicketId ?? undefined}
                    onBackToProject={
                      selectedTicketId && activeProjectData
                        ? () => setSelectedTicketId(null)
                        : undefined
                    }
                  />
                )}
              </div>
            </div>
          </>
        ) : has2PaneRight ? (
          <>
            {/* Collapsed Chat expand bar (2-pane) */}
            <button
              onClick={() => setChatCollapsed(false)}
              style={{
                width: chatCollapsed ? 32 : 0,
                opacity: chatCollapsed ? 1 : 0,
                overflow: "hidden",
                flexShrink: 0,
                pointerEvents: chatCollapsed ? "auto" : "none",
                transition:
                  "width 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1), background-color 150ms ease",
              }}
              className={`box-border flex h-full cursor-pointer items-center justify-center bg-puntt-neutral-gray-2 hover:bg-puntt-neutral-gray-3 ${
                chatCollapsed
                  ? "border-r border-puntt-neutral-gray-6"
                  : "border-0"
              }`}
              aria-label="Expand Chat pane"
              aria-hidden={!chatCollapsed}
              tabIndex={chatCollapsed ? 0 : -1}
              data-testid="pane-expand-chat"
            >
              <Text
                size="1"
                weight="medium"
                className="whitespace-nowrap uppercase tracking-wide text-puntt-neutral-gray-9"
                style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                }}
              >
                Chat
              </Text>
            </button>

            {/* Collapsed right-pane expand bar (2-pane) */}
            <button
              onClick={() => setArtworkCollapsed(false)}
              style={{
                width: artworkCollapsed ? 32 : 0,
                opacity: artworkCollapsed ? 1 : 0,
                overflow: "hidden",
                flexShrink: 0,
                pointerEvents: artworkCollapsed ? "auto" : "none",
                transition:
                  "width 300ms cubic-bezier(0.16,1,0.3,1), opacity 300ms cubic-bezier(0.16,1,0.3,1), background-color 150ms ease",
              }}
              className={`box-border flex h-full cursor-pointer items-center justify-center bg-puntt-neutral-gray-2 hover:bg-puntt-neutral-gray-3 ${
                artworkCollapsed
                  ? "border-r border-puntt-neutral-gray-6"
                  : "border-0"
              }`}
              aria-label="Expand pane"
              aria-hidden={!artworkCollapsed}
              tabIndex={artworkCollapsed ? 0 : -1}
              data-testid="pane-expand-artwork"
            >
              <Text
                size="1"
                weight="medium"
                className="whitespace-nowrap uppercase tracking-wide text-puntt-neutral-gray-9"
                style={{
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                }}
              >
                {effectiveViewTicketId
                  ? "Ticket"
                  : activeCanvas
                    ? "Canvas"
                    : "Details"}
              </Text>
            </button>

            {/* Right pane content (2-pane) */}
            <div
              style={{
                flexGrow: artworkCollapsed ? 0 : 1,
                flexShrink: 1,
                flexBasis: "0%",
                minWidth: 0,
                overflow: "hidden",
                transition: "flex-grow 300ms cubic-bezier(0.16,1,0.3,1)",
              }}
              className="animate-canvas-slide-in"
            >
              <div
                className={`h-full transition-[opacity,transform] duration-200 ease-out ${
                  artworkCollapsed
                    ? "translate-x-5 opacity-0"
                    : "translate-x-0 opacity-100"
                }`}
              >
                {activeProjectData && selectedTicketId ? (
                  <TicketViewPane
                    ticketId={selectedTicketId}
                    onCollapse={() => setArtworkCollapsed(true)}
                    onBackToProject={() => setSelectedTicketId(null)}
                    reviewStatus={reviewStatus}
                    onCitationClick={handleCitationClick}
                    onPinDragEnd={handlePinDragEnd}
                  />
                ) : isProjectChat && isProjectLoading && !activeProjectData ? (
                  <ProjectLoadingPane />
                ) : activeCanvas ? (
                  <div
                    className="relative h-full"
                    data-testid="ask-puntt-canvas-area"
                  >
                    <CanvasPane
                      canvas={activeCanvas.canvas}
                      onDismiss={handleDismissCanvas}
                      skillName={
                        activeCanvas.skill
                          ? SKILL_LABELS[activeCanvas.skill]
                          : undefined
                      }
                      chatId={chatId}
                      isLoading={isCanvasLoading}
                      onSectionClick={handleSectionClick}
                    />
                  </div>
                ) : displayProjectData ? (
                  <ProjectStatusPane
                    projectData={displayProjectData}
                    activeTicketId={effectiveViewTicketId}
                    ticketIds={chatData?.chat?.ticketIds ?? []}
                    onTicketSelect={setSelectedTicketId}
                    chatId={chatId}
                    chatCreatedAt={chatData?.chat?.createdAt}
                    onSendMessage={(msg) => handleSend(msg)}
                    onProjectDataUpdate={handleProjectDataUpdate}
                    actionsDisabled={sendMessage.isPending || waitingForAI}
                    isLoading={isProjectLoading}
                  />
                ) : effectiveViewTicketId ? (
                  <TicketViewPane
                    ticketId={effectiveViewTicketId}
                    onCollapse={() => setArtworkCollapsed(true)}
                    reviewStatus={reviewStatus}
                    onCitationClick={handleCitationClick}
                    onPinDragEnd={handlePinDragEnd}
                    reviewIndex={selectedReviewIndex}
                    totalReviews={allReviewTicketIds.length}
                    onPrevReview={handlePrevReview}
                    onNextReview={handleNextReview}
                  />
                ) : null}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AskPunttDropZone>
  );
}
